/**
 * DocumentStatusModule — P1BR-S1 → P1UNO
 * Un solo panel de estado y proceso en P1 (maqueta 3g sellada por Luis,
 * `.ptel/P1UNO_UN_PANEL_MAQUETA_20260925.html`): tres fases con la misma
 * geometría, para que nada cambie de sitio entre ellas.
 *
 *  - `normalizando`:   cabecera «NORMALIZANDO» · N infraestructuras · «↳ fila»
 *                      · n/total (%) · serpiente; barra lisa que se rellena.
 *  - `geolocalizando`: misma cabecera con «GEOLOCALIZANDO»; la barra se pinta
 *                      por calidad conforme llegan los resultados
 *                      (`progress.porCalidad`, sembrado en `useAutoGeocoding`)
 *                      y el tramo pendiente queda en la pista.
 *  - `terminado`:      «ESTADO DEL DOCUMENTO» · N infraestructuras ·
 *                      «% con puntuación ≥ 70» (P2VOC-c: ya no «aprobados»);
 *                      los 5 tramos desde `data`.
 *
 *  Bajo la barra, una fila de ejes sin saltos: ORIGEN (documento · corregida
 *  · geocodificada · centroide) a la izquierda y CALIDAD (≥90 · 70-89 · 50-69
 *  · <50 · sin coord.) a la derecha, separadas por «│»; «pendientes N» al
 *  final solo en proceso. Ceros atenuados. Tooltips `title` en cada elemento
 *  (DOP-4 a). La vía `crs` se funde en «corregida» (DOP-0b c).
 *
 * Vocabulario de tramos: AUDIT_P1BR §4 (eje primario: coordenadas finitas;
 * los tramos de puntuación solo con coordenadas). `computeDocumentStatus`
 * sigue siendo el helper puro del estado final.
 *
 * @version 2.0.0 - P1UNO: absorbe `ProgressBar` (modos processing/geocoding)
 * @version 2.1.0 - P1UNO-b: `ProgressBar` borrado; colores de origen desde
 *                  `colors.vias`; fila de ejes en una sola línea (hueco 8 px,
 *                  chips 11,5 px, etiquetas 10 px)
 * @version 2.2.0 - FILAPASOS (C′): la identidad del documento + ✕ «Cancelar
 *                  documento» es la primera línea del panel (`DocumentStrip`
 *                  variante `panel`); vuelve el velo animado, solo sobre el
 *                  tramo pendiente de la barra y solo en proceso.
 * @version 2.3.0 - INFDOC-2a: pastilla «Incidencias N» al final de la cabecera
 *                  (no en la fila de ejes, que en proceso ya no cabía en una
 *                  línea; Luis, opción d) (N = filas con alguna incidencia, DOP-1 b), «…» hasta
 *                  que P1 termina y resumen por tipo con `PTELTooltip` (ratón y
 *                  teclado, DOP-3 bis b). El módulo solo pinta: el resumen lo
 *                  calcula `Step1Page` (DOP-2 a). Sin prop, no hay pastilla.
 * @version 2.4.0 - INFDOC-2b: con detalle y alguna fila, la pastilla es un botón
 *                  (mismo aspecto, puntero de mano) que abre `PanelIncidencias`;
 *                  «Pulse para ver el detalle por fila» al pie del resumen
 *                  (DOP-6 a); al cerrar, el foco vuelve sin reabrir el resumen.
 * @version 2.5.0 - INFDOC-3b2: pasa a la ventana la orden de descarga del PDF
 *                  del ayuntamiento (`descargarInforme`); la prepara `Step1Page`.
 */
import React, { useInsertionEffect, useRef, useState, type FC, type CSSProperties } from 'react';
import { colors, typography, spacing, borderRadius } from '@/design-system/v2';
// Por ruta de fichero, no por el barril: los tests que mockean
// '@/design-system/v2' con factoría cerrada no lo conocen (HX-MOCK-FACTORY-BARREL-COUPLING).
import { SpinnerSnake } from '@/design-system/v2/components/SpinnerSnake';
import { PTELTooltip } from '@/components/ui/PTELTooltip';
import { PanelIncidencias, type DetalleIncidencias, type ResultadoDescargaInforme } from './PanelIncidencias';
import { DocumentStrip, type WizardDocumentContext } from '../shell/WizardContextBar';
import { type ViaBreakdown } from '@/utils/procedenciaVia';
import type { CalidadBreakdown } from '@/hooks/useAutoGeocoding';
import type { NormalizationRow } from './NormalizationTable';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Proyección de estado del documento (vocabulario sellado AUDIT_P1BR §4) */
export interface DocumentStatus {
  /** Infraestructuras cargadas */
  total: number;
  /** Con X/Y finitos */
  withCoords: number;
  /** Sin X/Y finitos (independiente del score) */
  withoutCoords: number;
  /** Math.round(withCoords / total × 100) — «% con coordenadas» */
  pctWithCoords: number;
  /** Score ≥90 (con coordenadas) */
  high: number;
  /** Score 70-89 (con coordenadas) */
  medium: number;
  /** Score 50-69 (con coordenadas) — «a revisar» */
  low: number;
  /** Score <50 o null, CON coordenadas — «puntuación baja» */
  lowScore: number;
  /** high + medium — «aprobados» */
  aprobados: number;
  /** Math.round(aprobados / total × 100) — «% con puntuación ≥ 70» */
  pctAprobados: number;
}

/** Fase del panel (P1UNO) */
export type PanelFase = 'normalizando' | 'geolocalizando' | 'terminado';

/**
 * Progreso de la fase en curso. Misma forma que `NormalizationProgress`
 * (`useNormalization`) y que `AutoGeocodingProgress` (`useAutoGeocoding`),
 * que además trae `porVia` y `porCalidad`.
 */
export interface ProcesoProgress {
  processed: number;
  total: number;
  percentage: number;
  currentInfraName?: string;
  porVia?: ViaBreakdown;
  porCalidad?: CalidadBreakdown;
}

/**
 * INFDOC-2a: lo que enseña la pastilla de incidencias. `pendiente` mientras P1
 * no ha terminado (las de coordenada solo valen al final, P1PROV) o mientras
 * se comprueba el límite del término; `lista` con el resumen ya en texto llano.
 */
export type PastillaIncidencias =
  | { estado: 'pendiente' }
  | {
      estado: 'lista';
      /** Filas con alguna incidencia (DOP-1 b) */
      filas: number;
      /** Propuesta B (Luis, 3-nov): un apartado por destinatario, solo los que tienen algo */
      grupos: GrupoIncidencias[];
      /** Filas sin comprobar el límite del término (DOP-5); ausente si no hay */
      aviso?: string;
      /** INFDOC-2b: detalle por fila; con él la pastilla se pulsa y abre la ventana (solo si `filas` > 0) */
      detalle?: DetalleIncidencias;
      /** INFDOC-3b2: prepara y descarga el PDF del ayuntamiento; sin ella, la ventana no tiene botón */
      descargarInforme?: () => Promise<ResultadoDescargaInforme>;
    };

/** Un apartado del resumen: «Para el ayuntamiento» o «Solo para el GREA» */
export interface GrupoIncidencias {
  titulo: string;
  /** Filas del apartado (las del GREA, solo las que no van también al ayuntamiento) */
  filas: number;
  /** Una línea por tipo: cifra (filas), texto llano y un apunte opcional en gris */
  lineas: Array<{ n: number; texto: string; apunte?: string }>;
}

export interface DocumentStatusModuleProps {
  /** Filas del documento normalizado (mismo estado que la tabla) */
  data: NormalizationRow[];
  /** Desglose por vía calculado de `data` (estado final) */
  viaBreakdown?: ViaBreakdown;
  /** Fase del panel (default: 'terminado') */
  fase?: PanelFase;
  /** Progreso de la fase en curso (requerido si fase ≠ 'terminado') */
  progress?: ProcesoProgress;
  /** Altura de la barra (default: 8px) */
  barHeight?: number;
  /** FILAPASOS: identidad del documento (primera línea del panel); null = sin línea */
  documento?: WizardDocumentContext | null;
  /** FILAPASOS: «Cancelar documento» (✕ en la primera línea) */
  onCancelarDocumento?: () => void;
  /** INFDOC-2a: pastilla de incidencias al final de la fila de ejes; sin ella, no se pinta */
  incidencias?: PastillaIncidencias;
}

// ─── Velo animado sobre el tramo pendiente (FILAPASOS DOP-3 a) ───────────────
// Referencia: `883b8a0f:ProgressBar.tsx` (gradiente blanco 15 %, 1,5 s).
// `@keyframes` inyectado una sola vez, patrón `SpinnerSnake`.

export const VELO_STYLE_ID = 'ptel-dsm-velo';

const VELO_CSS = `
@keyframes ptel-dsm-velo { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
.ptel-dsm-velo { position: absolute; inset: 0; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent); animation: ptel-dsm-velo 1.5s infinite; }
@media (prefers-reduced-motion: reduce) { .ptel-dsm-velo { animation: none; } }
`;

function injectVeloOnce(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(VELO_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = VELO_STYLE_ID;
  el.textContent = VELO_CSS;
  document.head.appendChild(el);
}

// ─── Helper puro (exportado para tests y para el cableado) ──────────────────

/**
 * Clasifica las filas según el vocabulario sellado.
 * Eje primario: coordenadas finitas. Una fila sin X/Y finitos cuenta como
 * «sin coordenadas» aunque tuviera score (defensivo). Una fila con
 * coordenadas y score null cae en «puntuación baja» (no en sin coordenadas),
 * separando así las dos poblaciones del antiguo bucket `critical`.
 */
export function computeDocumentStatus(data: NormalizationRow[]): DocumentStatus {
  const status: DocumentStatus = {
    total: data.length,
    withCoords: 0,
    withoutCoords: 0,
    pctWithCoords: 0,
    high: 0,
    medium: 0,
    low: 0,
    lowScore: 0,
    aprobados: 0,
    pctAprobados: 0,
  };

  data.forEach(row => {
    const hasCoords = Number.isFinite(row.x) && Number.isFinite(row.y);
    if (!hasCoords) {
      status.withoutCoords++;
      return;
    }
    status.withCoords++;
    const score = row.newScore;
    if (score == null || score < 50) status.lowScore++;
    else if (score >= 90) status.high++;
    else if (score >= 70) status.medium++;
    else status.low++;
  });

  status.aprobados = status.high + status.medium;
  if (status.total > 0) {
    status.pctWithCoords = Math.round((status.withCoords / status.total) * 100);
    status.pctAprobados = Math.round((status.aprobados / status.total) * 100);
  }
  return status;
}

// ─── Vocabulario de la fila de ejes ─────────────────────────────────────────

/** Tramos de calidad en orden de pintado (izq → dcha) y de chips */
const CALIDAD_ORDER = ['high', 'medium', 'low', 'lowScore', 'withoutCoords'] as const;
type TramoCalidad = (typeof CALIDAD_ORDER)[number];

/** Colores de tramo (DS v2; gris fg.muted para «sin coordenadas») */
const CALIDAD_COLORS: Record<TramoCalidad, string> = {
  high: colors.scores.high,         // lima  ≥90
  medium: colors.scores.medium,     // azul  70-89
  low: colors.scores.low,           // ámbar 50-69
  lowScore: colors.scores.critical, // rojo  <50 con coordenadas
  withoutCoords: colors.fg.muted,   // gris  sin coordenadas
};

const CALIDAD_CHIPS: ReadonlyArray<{ key: TramoCalidad; label: string; tip: string }> = [
  { key: 'high', label: '≥90', tip: 'Puntuación 90-100: ubicación contrastada por varias fuentes' },
  { key: 'medium', label: '70-89', tip: 'Puntuación 70-89: ubicación fiable' },
  { key: 'low', label: '50-69', tip: 'Puntuación 50-69: conviene revisar' },
  { key: 'lowScore', label: '<50', tip: 'Puntuación menor de 50: ubicación dudosa' },
  { key: 'withoutCoords', label: 'sin coord.', tip: 'Sin coordenada: no se pudo ubicar' },
];

/** Orígenes de la coordenada (DOP-0b c: `crs` se funde en «corregida») */
const ORIGEN_ORDER = ['documento', 'corregida', 'geocodificada', 'centroide'] as const;
type Origen = (typeof ORIGEN_ORDER)[number];

/** Colores de origen, distintos de los de calidad: `colors.vias` del DS v2 (P1UNO-b, DOP-2 a) */
const ORIGEN_COLORS: Record<Origen, string> = colors.vias;

const ORIGEN_CHIPS: ReadonlyArray<{ key: Origen; label: string; tip: string }> = [
  { key: 'documento', label: 'documento', tip: 'Coordenada tal como venía en el documento, validada' },
  { key: 'corregida', label: 'corregida', tip: 'Coordenada del documento corregida (dígito, zona o formato)' },
  { key: 'geocodificada', label: 'geocodificada', tip: 'Coordenada obtenida por dirección o nombre en fuentes oficiales' },
  { key: 'centroide', label: 'centroide', tip: 'Sin ubicación fiable: colocado en el centro del municipio; revisar' },
];

/** Proyección de `ViaBreakdown` (6 vías) a los 4 orígenes del panel */
export function origenesDesdeVias(vias?: ViaBreakdown): Record<Origen, number> {
  return {
    documento: vias?.directa ?? 0,
    corregida: (vias?.corregida ?? 0) + (vias?.crs ?? 0),
    geocodificada: vias?.geocodificada ?? 0,
    centroide: vias?.centroide ?? 0,
  };
}

const FASE_TITULO: Record<PanelFase, { texto: string; tip: string }> = {
  normalizando: { texto: 'Normalizando', tip: 'Fase en curso: lectura y normalización de las coordenadas del documento' },
  geolocalizando: { texto: 'Geolocalizando', tip: 'Fase en curso; el proceso sigue aunque cambies de pestaña' },
  terminado: { texto: 'Estado del documento', tip: 'Resumen del documento cargado tras normalizar y geolocalizar sus elementos' },
};

// ─── Estilos ────────────────────────────────────────────────────────────────

const MONO: CSSProperties = {
  fontFamily: typography.fontFamily.mono,
  fontWeight: typography.fontWeight.semibold,
  color: colors.fg.primary,
};

const STYLES = {
  container: (enProceso: boolean): CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[2],
    padding: `${spacing[3]} ${spacing[4]}`,
    marginBottom: spacing[4],
    // Fondo verde tenue en proceso, neutro al terminar (maqueta 3g)
    backgroundColor: enProceso ? 'rgba(163, 230, 53, 0.08)' : colors.bg.tertiary,
    border: `1px solid ${enProceso ? 'rgba(163, 230, 53, 0.25)' : colors.border.DEFAULT}`,
    borderRadius: borderRadius.lg,
    fontFamily: typography.fontFamily.sans,
    transition: 'background-color 0.3s ease, border-color 0.3s ease',
  }),

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: 0,
  } as CSSProperties,

  titulo: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  } as CSSProperties,

  recuento: {
    fontSize: typography.fontSize.sm,
    color: colors.fg.muted,
    whiteSpace: 'nowrap',
  } as CSSProperties,

  fila: {
    fontFamily: typography.fontFamily.mono,
    fontSize: '11px',
    color: colors.fg.muted,
    letterSpacing: '0.02em',
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as CSSProperties,

  espaciador: { flex: '1 1 auto' } as CSSProperties,

  contador: {
    fontSize: typography.fontSize.sm,
    color: colors.fg.muted,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  } as CSSProperties,

  pctAprobados: {
    fontSize: typography.fontSize.sm,
    color: colors.fg.secondary,
    whiteSpace: 'nowrap',
  } as CSSProperties,

  // SERP1 (DOP-3 a): pista con contraste (bg.secondary + borde interior por
  // token; el inset es borde, no glow). El movimiento lo pone la serpiente.
  pista: (height: number): CSSProperties => ({
    display: 'flex',
    width: '100%',
    height: `${height}px`,
    background: colors.bg.secondary,
    boxShadow: `inset 0 0 0 1px ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  }),

  tramo: (color: string, widthPercent: number, opacity = 1): CSSProperties => ({
    height: '100%',
    background: color,
    opacity,
    width: `${widthPercent}%`,
    transition: 'width 0.3s ease',
    minWidth: widthPercent > 0 ? '2px' : '0',
  }),

  // FILAPASOS: el resto de la pista (lo pendiente) lleva el velo en proceso
  pendiente: {
    position: 'relative',
    flex: '1 1 auto',
    height: '100%',
    overflow: 'hidden',
  } as CSSProperties,

  // P1UNO-b (DOP-3 a): hueco 8 px, chips 11,5 px y etiquetas de eje 10 px para
  // que la fila quepa en una línea con «pendientes NNN» (medido en Guadix,
  // 266 filas: 13 hijos + 12 huecos frente a 1039 px disponibles).
  ejes: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px 8px',
    fontSize: '11.5px',
    color: colors.fg.secondary,
    minWidth: 0,
  } as CSSProperties,

  eje: {
    fontSize: '10px',
    fontWeight: typography.fontWeight.medium,
    color: colors.fg.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  } as CSSProperties,

  chip: (cero: boolean): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    whiteSpace: 'nowrap',
    opacity: cero ? 0.45 : 1,
  }),

  punto: (color: string): CSSProperties => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  }),

  cifra: (color: string): CSSProperties => ({ ...MONO, color }),

  separador: {
    color: colors.fg.subtle,
    padding: '0 2px',
  } as CSSProperties,

  pendientes: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    whiteSpace: 'nowrap',
    color: colors.fg.muted,
  } as CSSProperties,

  // INFDOC-2a: pastilla de la maqueta de INFDOC-0 (borde y texto ámbar; gris
  // mientras se calcula o si no hay ninguna). Sin sombra ni brillo.
  incidencias: (activa: boolean): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    whiteSpace: 'nowrap',
    padding: '1px 9px',
    borderRadius: '999px',
    border: `1px solid ${activa ? colors.warning : colors.border.DEFAULT}`,
    color: activa ? colors.warning : colors.fg.muted,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    flexShrink: 0,
    cursor: 'default',
  }),
  // INFDOC-2b: la misma pastilla como botón; solo cambia el puntero (DOP-6 a)
  incidenciasBoton: {
    background: 'transparent',
    fontFamily: 'inherit',
    lineHeight: 'inherit',
    margin: 0,
    cursor: 'pointer',
  } as CSSProperties,

  // Propuesta B (Luis, 3-nov-2026): apartados por destinatario, cifras en su
  // propia columna alineada a la derecha y nota al pie; colores del DS v2.
  resumen: { display: 'flex', flexDirection: 'column', fontSize: '13px', lineHeight: 1.4 } as CSSProperties,
  resumenTitulo: { fontWeight: typography.fontWeight.semibold, color: colors.fg.primary } as CSSProperties,
  resumenApartado: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    margin: '10px 0 4px',
    paddingBottom: '4px',
    borderBottom: `1px solid ${colors.border.DEFAULT}`,
  } as CSSProperties,
  resumenLinea: {
    display: 'grid',
    gridTemplateColumns: '34px 1fr',
    gap: '10px',
    alignItems: 'baseline',
    padding: '3px 0',
  } as CSSProperties,
  resumenPie: {
    borderTop: `1px solid ${colors.border.DEFAULT}`,
    marginTop: '8px',
    paddingTop: '8px',
    fontSize: '12px',
    color: colors.fg.muted,
  } as CSSProperties,
};

/** INFDOC-2a: pastilla «Incidencias N» con su resumen (DOP-3 a, DOP-3 bis b, DOP-5; propuesta B) */
const PastillaIncidenciasView: FC<{ incidencias: PastillaIncidencias }> = ({ incidencias }) => {
  const lista = incidencias.estado === 'lista';
  const activa = lista && incidencias.filas > 0;
  // INFDOC-2b: se pulsa solo con detalle y alguna fila (DOP-6 a: nunca una ventana vacía)
  const detalle = lista && incidencias.filas > 0 ? incidencias.detalle : undefined;
  const [abierto, setAbierto] = useState(false);
  const boton = useRef<HTMLButtonElement>(null);
  /** El próximo foco viene de cerrar la ventana: no reabrir el resumen */
  const sinResumen = useRef(false);
  const contenido = lista ? (
    <div data-testid="dsm-incidencias-resumen" style={STYLES.resumen}>
      <span style={STYLES.resumenTitulo}>
        {incidencias.filas === 0
          ? 'Sin incidencias en el documento'
          : `${incidencias.filas} ${incidencias.filas === 1 ? 'fila' : 'filas'} con incidencias`}
      </span>
      {incidencias.grupos.map(g => (
        <div key={g.titulo} data-testid="dsm-incidencias-grupo">
          <div style={STYLES.resumenApartado}>
            <span style={STYLES.resumenTitulo}>{g.titulo}</span>
            <span style={{ ...MONO, fontWeight: typography.fontWeight.medium, color: colors.warning }}>
              {g.filas} {g.filas === 1 ? 'fila' : 'filas'}
            </span>
          </div>
          {g.lineas.map(l => (
            <div key={l.texto} style={STYLES.resumenLinea}>
              <span style={{ ...MONO, fontWeight: typography.fontWeight.medium, textAlign: 'right' }}>{l.n}</span>
              <span style={{ color: colors.fg.secondary }}>
                {l.texto}
                {l.apunte && <span style={{ color: colors.fg.muted }}> · {l.apunte}</span>}
              </span>
            </div>
          ))}
        </div>
      ))}
      {incidencias.aviso && (
        <span data-testid="dsm-incidencias-aviso" style={{ ...STYLES.resumenPie, color: colors.warning }}>
          {incidencias.aviso}
        </span>
      )}
      {incidencias.filas > 0 && (
        <span style={STYLES.resumenPie}>
          Una fila puede tener varias incidencias; la cifra de la pastilla cuenta filas.
          {/* INFDOC-2b (DOP-6 a): solo cuando la pastilla se pulsa */}
          {detalle && <span data-testid="dsm-incidencias-pulse" style={{ display: 'block', marginTop: '4px' }}>
            Pulse para ver el detalle por fila.
          </span>}
        </span>
      )}
    </div>
  ) : (
    <span>Se calculan cuando termina la geolocalización.</span>
  );
  const comunes = {
    'data-testid': 'dsm-incidencias',
    'data-estado': incidencias.estado,
    'data-count': lista ? incidencias.filas : undefined,
    'aria-label': lista
      ? `Incidencias del documento: ${incidencias.filas} ${incidencias.filas === 1 ? 'fila' : 'filas'}`
      : 'Incidencias del documento: pendientes de calcular',
  };
  const interior = (
    <>
      <span>Incidencias</span>
      <span style={{ ...MONO, color: 'inherit' }}>{lista ? incidencias.filas : '…'}</span>
    </>
  );
  return (
    <>
      <PTELTooltip content={contenido} position="top" className="max-w-[380px] px-3.5 py-3">
        {detalle ? (
          // INFDOC-2b: botón con el mismo aspecto que la pastilla (la foto de VA07-02 no cambia)
          <button
            {...comunes}
            ref={boton}
            type="button"
            aria-haspopup="dialog"
            onClick={() => setAbierto(true)}
            // Al volver el foco desde la ventana, Radix reabriría el resumen (AUDIT_INFDOC2B §1.2)
            onFocus={e => { if (sinResumen.current) { sinResumen.current = false; e.preventDefault(); } }}
            style={{ ...STYLES.incidencias(activa), ...STYLES.incidenciasBoton }}
          >
            {interior}
          </button>
        ) : (
          <span {...comunes} tabIndex={0} style={STYLES.incidencias(activa)}>{interior}</span>
        )}
      </PTELTooltip>
      {detalle && lista && (
        <PanelIncidencias
          abierto={abierto}
          onAbiertoChange={setAbierto}
          filas={incidencias.filas}
          detalle={detalle}
          onDescargarInforme={incidencias.descargarInforme}
          onCloseAutoFocus={e => {
            e.preventDefault();
            sinResumen.current = true;
            boton.current?.focus();
          }}
        />
      )}
    </>
  );
};

// ─── Component ──────────────────────────────────────────────────────────────

const CEROS: Record<TramoCalidad, number> = { high: 0, medium: 0, low: 0, lowScore: 0, withoutCoords: 0 };

export const DocumentStatusModule: FC<DocumentStatusModuleProps> = ({
  data,
  viaBreakdown,
  fase = 'terminado',
  progress,
  barHeight = 8,
  documento,
  onCancelarDocumento,
  incidencias,
}) => {
  useInsertionEffect(injectVeloOnce, []);
  const enProceso = fase !== 'terminado';
  const status = computeDocumentStatus(data);
  // Durante la normalización `data` puede ser aún la extracción cruda:
  // el total sale del progreso si las filas no han llegado.
  const total = data.length > 0 ? data.length : (enProceso ? (progress?.total ?? 0) : 0);

  if (total === 0) return null;

  // Calidad: en geolocalización, la foto acumulada del hook (sembrada);
  // al terminar, `data`; normalizando, ceros (la barra se rellena lisa).
  const calidad: Record<TramoCalidad, number> =
    fase === 'terminado'
      ? { high: status.high, medium: status.medium, low: status.low, lowScore: status.lowScore, withoutCoords: status.withoutCoords }
      : fase === 'geolocalizando' && progress?.porCalidad
        ? progress.porCalidad
        : CEROS;
  const origenes = origenesDesdeVias(
    fase === 'terminado' ? viaBreakdown : fase === 'geolocalizando' ? progress?.porVia : undefined,
  );
  const pendientes = enProceso && progress ? Math.max(0, progress.total - progress.processed) : 0;
  const pct = progress ? Math.min(100, Math.max(0, progress.percentage)) : 0;
  const titulo = FASE_TITULO[fase];
  const ariaBarra = enProceso
    ? `${titulo.texto}: ${progress?.processed ?? 0} de ${progress?.total ?? 0} procesadas`
    : `Estado del documento: ${status.aprobados} con puntuación de 70 o más, ${status.low} a revisar, ` +
      `${status.lowScore} con puntuación baja, ${status.withoutCoords} sin coordenadas de ${status.total}`;

  return (
    <div data-testid="document-status-module" data-fase={fase} style={STYLES.container(enProceso)}>
      {/* FILAPASOS (C′): primera línea = identidad del documento + ✕, con raya tenue */}
      {documento && (
        <DocumentStrip documento={documento} variant="panel" onCancel={onCancelarDocumento} />
      )}

      {/* Cabecera en una línea: fase · recuento · fila en curso · cifra + serpiente / % aprobados */}
      <div data-testid="dsm-header" style={STYLES.header}>
        <span data-testid="dsm-fase" data-fase={fase} title={titulo.tip} style={STYLES.titulo}>
          {titulo.texto}
        </span>
        <span data-testid="dsm-total" title="Elementos extraídos del documento" style={STYLES.recuento}>
          <span style={MONO}>{total}</span>{' infraestructuras'}
        </span>
        {enProceso && progress?.currentInfraName ? (
          <span data-testid="dsm-fila" title="Elemento que se está procesando ahora" style={STYLES.fila}>
            ↳ {progress.currentInfraName}
          </span>
        ) : (
          <span style={STYLES.espaciador} />
        )}
        {enProceso && progress ? (
          <span data-testid="dsm-contador" title="Elementos procesados sobre el total" style={STYLES.contador}>
            <span>
              <span style={MONO}>{progress.processed}</span>/{progress.total}{' '}
              (<span style={MONO}>{pct}%</span>)
            </span>
            {/* SERP1 (DOP-2 a): serpiente a la derecha de la cifra */}
            <SpinnerSnake />
          </span>
        ) : (
          <span
            data-testid="dsm-pct-aprobados"
            title="Elementos con puntuación 70 o más, sobre el total"
            style={STYLES.pctAprobados}
          >
            <span style={MONO}>{status.pctAprobados}{'\u00A0'}%</span>
            {' con puntuación ≥\u00A070'}
          </span>
        )}
        {/* INFDOC-2a (Luis, 3-nov-2026, opción d): en la cabecera y no en la fila de
            ejes, que con «pendientes N» ya no cabía en una línea (P1UNO-b DOP-3 a). */}
        {incidencias && <PastillaIncidenciasView incidencias={incidencias} />}
      </div>

      {/* Una sola barra: lisa en normalización, por calidad después */}
      <div data-testid="dsm-bar" role="img" aria-label={ariaBarra} style={STYLES.pista(barHeight)}>
        {fase === 'normalizando' ? (
          <div
            data-testid="dsm-normalizando-fill"
            title="Avance de la normalización"
            style={STYLES.tramo(colors.success, pct, 0.55)}
          />
        ) : (
          CALIDAD_CHIPS.map(({ key, tip }) => (
            <div
              key={key}
              data-testid={`dsm-segment-${key}`}
              data-count={calidad[key]}
              title={`${tip} · ${calidad[key]}`}
              style={STYLES.tramo(CALIDAD_COLORS[key], (calidad[key] / total) * 100)}
            />
          ))
        )}
        {/* FILAPASOS (DOP-3 a): velo animado solo sobre lo pendiente y solo en proceso */}
        {enProceso && (
          <div data-testid="dsm-tramo-pendiente" title="Tramo pendiente de procesar" style={STYLES.pendiente}>
            <span data-testid="dsm-velo" className="ptel-dsm-velo" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Fila de ejes: ORIGEN │ CALIDAD (+ pendientes en proceso) */}
      <div data-testid="dsm-ejes" style={STYLES.ejes}>
        <span style={STYLES.eje} title="De dónde sale la coordenada de cada elemento">Origen</span>
        {ORIGEN_CHIPS.map(({ key, label, tip }) => (
          <span
            key={key}
            data-testid={`dsm-origen-${key}`}
            data-count={origenes[key]}
            title={tip}
            style={STYLES.chip(origenes[key] === 0)}
          >
            <span style={STYLES.punto(ORIGEN_COLORS[key])} aria-hidden="true" />
            <span>{label}</span>
            <span style={STYLES.cifra(ORIGEN_COLORS[key])}>{origenes[key]}</span>
          </span>
        ))}
        <span style={STYLES.separador} aria-hidden="true">│</span>
        <span style={STYLES.espaciador} />
        <span style={STYLES.eje} title="Fiabilidad de la ubicación, de 0 a 100">Calidad</span>
        {CALIDAD_CHIPS.map(({ key, label, tip }) => (
          <span
            key={key}
            data-testid={`dsm-calidad-${key}`}
            data-count={calidad[key]}
            title={tip}
            style={STYLES.chip(calidad[key] === 0)}
          >
            <span style={STYLES.punto(CALIDAD_COLORS[key])} aria-hidden="true" />
            <span>{label}</span>
            <span style={STYLES.cifra(key === 'withoutCoords' ? colors.fg.muted : CALIDAD_COLORS[key])}>
              {calidad[key]}
            </span>
          </span>
        ))}
        {enProceso && (
          <span data-testid="dsm-pendientes" title="Elementos que aún no se han procesado" style={STYLES.pendientes}>
            <span>pendientes</span>
            <span style={MONO}>{pendientes}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default DocumentStatusModule;
